import React from "react";
import { Box } from "@mui/material";
import { LineChart } from "@mui/x-charts";
import { pink } from "@mui/material/colors";
import dayjs from "dayjs";

interface TotalValue {
  endTime: string;
  startTime: string;
  values: {
    type: string;
    value: number;
  }[];
}

interface ProductionForecastChartsProps {
  totalValues: TotalValue[];
  xAxisLength?: number;
  width?: number;
}

interface SeriesData {
  data: number[];
  id: number;
  label: string;
  showMark: boolean;
  color?: string;
}

const ProductionForecastChart = ({
  totalValues,
  xAxisLength,
  width,
}: ProductionForecastChartsProps) => {
  const xAxisData = Array.from({ length: xAxisLength }, (_, i) => i);

  const xAxis = [
    {
      data: xAxisData,
      valueFormatter: (value: number) => {
        return dayjs(totalValues[value]?.startTime).format("HH:mm");
      },
    },
  ];

  const series: SeriesData[] = [];

  const forecastLine = totalValues.map((interval) => {
    return Math.round(
      interval.values.find((value) => value.type === "FORECAST").value,
    );
  });

  const forecaLine = totalValues.map((interval) => {
    return Math.round(
      interval.values.find((value) => value.type === "FORECA")?.value,
    );
  });

  const meteologicaLine = totalValues.map((interval) => {
    return Math.round(
      interval.values.find((value) => value.type === "METEOLOGICA")?.value,
    );
  });

  const aiolosLine = totalValues.map((interval) => {
    return Math.round(
      interval.values.find((value) => value.type === "AIOLOS")?.value,
    );
  });

  const conwxLine = totalValues.map((interval) => {
    return Math.round(
      interval.values.find((value) => value.type === "CONWX")?.value ?? 0,
    );
  });

  const realProductionLine = totalValues.map((interval) => {
    return Math.round(
      interval.values.find((value) => value.type === "REAL_PRODUCTION")
        ?.value ?? 0,
    );
  });

  series.push({
    data: forecastLine,
    id: 10,
    label: "Forecast",
    color: pink.A400,
    showMark: false,
  });

  if (meteologicaLine && meteologicaLine.some((x) => x > 0)) {
    series.push({
      data: meteologicaLine,
      id: 12,
      label: "Meteologica",
      color: "#f9f098",
      showMark: false,
    });
  }

  if (aiolosLine && aiolosLine.some((x) => x > 0)) {
    series.push({
      data: aiolosLine,
      id: 13,
      label: "Aiolos",
      color: "#9ddba1",
      showMark: false,
    });
  }

  if (forecaLine && forecaLine.some((x) => x > 0)) {
    series.push({
      data: forecaLine,
      id: 14,
      label: "Foreca",
      color: "#d0a8ff",
      showMark: false,
    });
  }

  if (conwxLine && conwxLine.some((x) => x > 0)) {
    series.push({
      data: conwxLine,
      id: 15,
      label: "ConWX",
      color: "#8bb5ff",
      showMark: false,
    });
  }

  if (realProductionLine && realProductionLine.some((x) => x > 0)) {
    series.push({
      data: realProductionLine,
      id: 16,
      label: "Real production",
      color: "#000000",
      showMark: false,
    });
  }

  const allData = [...series.flatMap((s) => s.data), ...forecastLine];
  const calculatedMin = Math.min(...allData);
  const calculatedMax = Math.max(...allData);

  const yAxis = [
    {
      min: calculatedMin - 0.1,
      max: calculatedMax + 0.1,
      valueFormatter: (value: number) => value.toString(),
    },
  ];

  return (
    <Box>
      <LineChart
        xAxis={xAxis}
        yAxis={yAxis}
        series={series}
        width={width}
        height={380}
        grid={{ vertical: true, horizontal: true }}
        margin={{ left: 40, right: 40, top: 40, bottom: 90 }}
        colors={[
          "#fbb4ae",
          "#b3cde3",
          "#ccebc5",
          "#decbe4",
          "#fed9a6",
          "#ffffcc",
          "#e5d8bd",
          "#fddaec",
          "#f2f2f2",
        ]}
        slotProps={{
          legend: {
            position: {
              vertical: "bottom",
              horizontal: "middle",
            },
            itemMarkWidth: 15,
            itemMarkHeight: 2,
            markGap: 5,
            itemGap: 5,
          },
        }}
        sx={{
          "& .MuiChartsLegend-series text": { fontSize: "12px !important" },
          "& .MuiLineElement-series-10": {
            strokeWidth: 4,
          },
          "& .MuiLineElement-series-12": {
            strokeDasharray: "5 3",
          },
          "& .MuiLineElement-series-13": {
            strokeDasharray: "5 3",
          },
          "& .MuiLineElement-series-14": {
            strokeDasharray: "5 3",
          },
          padding: "5px",
        }}
      />
    </Box>
  );
};

export default ProductionForecastChart;
